import React, { useState, useEffect } from 'react';
import { Box, Toolbar, AppBar, Typography, IconButton, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Avatar, Menu, MenuItem, useTheme, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SchoolIcon from '@mui/icons-material/School';
import ChatIcon from '@mui/icons-material/Chat';
import MailIcon from '@mui/icons-material/Mail';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import Badge from '@mui/material/Badge';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ReportProblemModal from '../common/ReportProblemModal';
import api from '../../services/api';
import TourIcon from '@mui/icons-material/Tour';
import { useLearnerTour } from '../../hooks/useLearnerTour';

const drawerWidth = 260;

/**
 * Props for the LearnerLayout component
 */
interface LearnerLayoutProps {
    /** The content to render within the layout */
    children: React.ReactNode;
}

/**
 * Layout component for the Learner interface.
 * It features a responsive sidebar navigation, a top app bar, and a content area.
 *
 * @param props - The props for the component
 * @returns The Learner Layout UI
 */
const LearnerLayout: React.FC<LearnerLayoutProps> = ({ children }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [timeConnected, setTimeConnected] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { startTour } = useLearnerTour();
    const [stats, setStats] = useState({ completedLessons: 0 });

    // Extract organization branding with defaults
    const organization = typeof user?.organizationId === 'object' ? user.organizationId : null;
    const logoUrl = organization?.logoUrl || '/logo.png';
    const primaryColor = organization?.theme?.primaryColor || '#1976d2';
    const secondaryColor = organization?.theme?.secondaryColor || '#1565c0';

    const [pendingSurveysCount, setPendingSurveysCount] = useState(0);
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

    // Fetch user stats for tour button logic
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await api.get('/api/progress/stats');
                setStats(res.data);
            } catch (err) {
                console.error("Error fetching stats for layout", err);
            }
        };
        if (user) fetchStats();
    }, [user]);

    // Mock timer for "Time Connected"
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeConnected(prev => prev + 1);
        }, 60000); // Update every minute
        return () => clearInterval(timer);
    }, []);


    // Fetch pending surveys count
    useEffect(() => {
        const fetchPendingCount = async () => {
            try {
                const res = await api.get('/api/satisfaction/pending');
                setPendingSurveysCount(res.data.pendingCount || 0);
            } catch (err) {
                console.error("Error fetching pending surveys", err);
            }
        };
        if (user) fetchPendingCount();
    }, [user]);

    // Fetch total unread chat messages
    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const res = await api.get('/api/chat/unread-count');
                setUnreadMessagesCount(res.data.count || 0);
            } catch (err) {
                console.error("Error fetching unread chat count", err);
            }
        };
        if (user) fetchUnreadCount();
    }, [user]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const formatTime = (minutes: number) => {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${h}h ${m}m`;
    };

    const menuItems = [
        { text: 'Tableau de bord', icon: <DashboardIcon />, path: '/learner/dashboard' },
        { text: 'Mes Parcours', icon: <SchoolIcon />, path: '/learner/parcours' },
        { text: 'Chat', icon: <ChatIcon />, path: '/learner/chat', badge: unreadMessagesCount },
        { text: 'Messages', icon: <MailIcon />, path: '/learner/emails' },
        { text: 'Enquêtes', icon: <AssignmentIcon />, path: '/learner/surveys', badge: pendingSurveysCount },
        { text: 'Évaluations', icon: <AssignmentTurnedInIcon />, path: '/learner/evaluations' },
        { text: 'Profil', icon: <PersonIcon />, path: '/learner/profile' },
    ];

    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                <img
                    src={logoUrl}
                    alt="Logo"
                    style={{
                        height: '50px',
                        width: 'auto',
                        objectFit: 'contain'
                    }}
                />
            </Toolbar>
            <Divider sx={{ borderColor: 'rgba(0,0,0,0.05)' }} />
            <List sx={{ px: 2, pt: 2, flexGrow: 1 }}>
                {menuItems.map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                        <ListItemButton
                            id={`menu-item-${item.text.replace(/\s+/g, '-').toLowerCase()}`}
                            onClick={() => navigate(item.path)}
                            selected={location.pathname === item.path}
                            sx={{
                                borderRadius: 3,
                                py: 1.5,
                                transition: 'all 0.3s ease',
                                '&.Mui-selected': {
                                    bgcolor: primaryColor,
                                    color: 'white',
                                    boxShadow: `0 4px 12px ${primaryColor}40`,
                                    '& .MuiListItemIcon-root': {
                                        color: 'white',
                                    },
                                    '&:hover': {
                                        bgcolor: secondaryColor,
                                    }
                                },
                                '&:hover': {
                                    bgcolor: `${primaryColor}14`,
                                    transform: 'translateX(5px)'
                                }
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40, color: location.pathname === item.path ? 'white' : 'text.secondary' }}>
                                {item.badge && item.badge > 0 ? (
                                    <Badge badgeContent={item.badge} color="error">
                                        {item.icon}
                                    </Badge>
                                ) : (
                                    item.icon
                                )}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                    fontFamily: 'Quicksand, sans-serif',
                                    fontWeight: location.pathname === item.path ? 600 : 500
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>

            <Box sx={{ p: 2 }}>
                {stats.completedLessons > 0 && (
                    <ListItemButton
                        onClick={startTour}
                        sx={{
                            borderRadius: 3,
                            py: 1.5,
                            mb: 1,
                            color: 'primary.main',
                            bgcolor: 'rgba(25, 118, 210, 0.08)',
                            '&:hover': {
                                bgcolor: 'rgba(25, 118, 210, 0.12)',
                            }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                            <TourIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Revoir la visite"
                            primaryTypographyProps={{
                                fontFamily: 'Quicksand, sans-serif',
                                fontWeight: 600
                            }}
                        />
                    </ListItemButton>
                )}
                <ListItemButton
                    id="menu-item-report-problem"
                    onClick={() => setReportModalOpen(true)}
                    sx={{
                        borderRadius: 3,
                        py: 1.5,
                        color: 'error.main',
                        bgcolor: 'error.lighter', // Assuming theme has this or fallback
                        '&:hover': {
                            bgcolor: 'rgba(211, 47, 47, 0.08)',
                        }
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
                        <ReportProblemIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary="Signaler un problème"
                        primaryTypographyProps={{
                            fontFamily: 'Quicksand, sans-serif',
                            fontWeight: 600
                        }}
                    />
                </ListItemButton>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', height: '100vh', width: '100vw', bgcolor: '#f4f6f8', overflow: 'hidden' }}>
            <AppBar
                position="fixed"
                sx={{
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: `${drawerWidth}px` },
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(12px)',
                    color: 'text.primary',
                    boxShadow: 'none',
                    borderBottom: '1px solid rgba(0,0,0,0.05)'
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: primaryColor,
                            bgcolor: `${primaryColor}14`,
                            px: 2,
                            py: 0.8,
                            borderRadius: 4,
                            border: `1px solid ${primaryColor}1A`
                        }}>
                            <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
                                Session: {formatTime(timeConnected)}
                            </Typography>
                        </Box>
                    </Box>

                    <Box>
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenu}
                            sx={{
                                p: 0.5,
                                border: '2px solid transparent',
                                transition: 'all 0.2s',
                                '&:hover': { border: `2px solid ${primaryColor}` }
                            }}
                        >
                            <Avatar
                                src={user?.preferences?.photoUrl}
                                sx={{ bgcolor: primaryColor, width: 40, height: 40, fontSize: '1rem', fontWeight: 'bold' }}
                            >
                                {user?.firstName?.charAt(0) || 'U'}
                            </Avatar>
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            PaperProps={{
                                sx: {
                                    mt: 1.5,
                                    borderRadius: 3,
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                    border: '1px solid rgba(0,0,0,0.05)'
                                }
                            }}
                        >
                            <MenuItem onClick={() => { handleClose(); navigate('/learner/profile'); }} sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                                <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                                Profil
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={handleLogout} sx={{ fontFamily: 'Quicksand, sans-serif', color: 'error.main' }}>
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" color="error" />
                                </ListItemIcon>
                                Déconnexion
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            borderRight: 'none',
                            boxShadow: '4px 0 24px rgba(0,0,0,0.1)'
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            borderRight: '1px solid rgba(0,0,0,0.05)',
                            bgcolor: 'rgba(255,255,255,0.8)',
                            backdropFilter: 'blur(10px)'
                        },
                    }}
                    open
                >
                    {drawerContent}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 2, md: 4 },
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    mt: 8,
                    height: 'calc(100vh - 64px)',
                    overflowY: 'auto',
                    backgroundImage: 'radial-gradient(circle at 10% 20%, rgb(242, 246, 252) 0%, rgb(236, 240, 249) 90%)',
                    WebkitOverflowScrolling: 'touch'
                }}
            >
                {children}
            </Box>
            <ReportProblemModal open={reportModalOpen} onClose={() => setReportModalOpen(false)} />
        </Box>
    );
};

export default LearnerLayout;
